import type { AlgoliaQueryState } from '../../templates/components/collections/searchStateUrlStorageHelpers';

const normalizeFilterToken = (value: string): string => value.trim().replace(/[^a-z0-9]/gi, '');

const normalizeFilterName = (key: string): string => normalizeFilterToken(key.replace(/^facets\./, ''));

const normalizeFilterValues = (value: unknown): Array<string> => {
  if (Array.isArray(value)) {
    return value
      .filter(item => item !== undefined && item !== null && String(item).trim() !== '')
      .map(item => normalizeFilterToken(String(item)))
      .filter(Boolean);
  }

  if (value === undefined || value === null || value === '') {
    return [];
  }

  const normalizedValue = normalizeFilterToken(String(value));

  return normalizedValue ? [normalizedValue] : [];
};

const buildFilterTuple = (name: string, operator: string, values: Array<string>): string =>
  `(${name},${operator},${values.join(',')})`;

export const buildCriteoFiltersFromSearchState = (
  searchState?: Pick<AlgoliaQueryState, 'refinementList' | 'range' | 'toggle'>
): string | undefined => {
  const filterTuples: Array<string> = [];

  Object.entries(searchState?.refinementList ?? {}).forEach(([key, value]) => {
    const normalizedFilterName = normalizeFilterName(key);
    const values = normalizeFilterValues(value);

    if (!normalizedFilterName || !values.length) {
      return;
    }

    filterTuples.push(buildFilterTuple(normalizedFilterName, values.length > 1 ? 'in' : 'eq', values));
  });

  const priceRange = searchState?.range?.['facets.Price'];

  if (priceRange) {
    const [minPrice = '', maxPrice = ''] = String(priceRange).split(':');
    const normalizedPriceFilterName = normalizeFilterName('facets.Price');
    const normalizedMinPrice = normalizeFilterToken(minPrice);
    const normalizedMaxPrice = normalizeFilterToken(maxPrice);

    if (normalizedPriceFilterName && normalizedMinPrice) {
      filterTuples.push(buildFilterTuple(normalizedPriceFilterName, 'ge', [normalizedMinPrice]));
    }

    if (normalizedPriceFilterName && normalizedMaxPrice) {
      filterTuples.push(buildFilterTuple(normalizedPriceFilterName, 'le', [normalizedMaxPrice]));
    }
  }

  if (searchState?.toggle?.onPromotion) {
    filterTuples.push(buildFilterTuple(normalizeFilterName('onPromotion'), 'eq', ['true']));
  }

  if (searchState?.toggle?.['banner_tags.label']) {
    filterTuples.push(buildFilterTuple(normalizeFilterName('banner_tags.label'), 'eq', ['Clearance']));
  }

  if (searchState?.toggle?.isMarketplace) {
    filterTuples.push(buildFilterTuple(normalizeFilterName('isMarketplace'), 'eq', ['false']));
  }

  return filterTuples.length ? filterTuples.join(',') : undefined;
};
